import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, X } from 'lucide-react';

/**
 * Inline success banner shown after Stripe Checkout / Billing Portal returns
 * the user with `?upgradeSuccess=1` (or `?upgradeSuccess=payment-method`).
 * Mirrors the retailers-dashboard banner so the post-redirect handoff feels
 * familiar to migrated retailers.
 */
export function UpgradeSuccessAlert() {
	const [params, setParams] = useSearchParams();
	const flag = params.get('upgradeSuccess');
	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		setDismissed(false);
	}, [flag]);

	if (!flag || dismissed) return null;

	const heading = flag === 'payment-method' ? 'Card updated' : 'Subscription updated';
	const message =
		flag === 'payment-method'
			? 'Your new card is on file — future Unify charges will use it.'
			: 'Your plan change is live. The next invoice will reflect it.';

	const close = () => {
		setDismissed(true);
		const next = new URLSearchParams(params);
		next.delete('upgradeSuccess');
		setParams(next, { replace: true });
	};

	return (
		<div className="flex items-start gap-3 rounded-xl border border-success/40 bg-success-soft p-4 text-success-soft-foreground">
			<CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold">{heading}</div>
				<div className="mt-0.5 text-sm">{message}</div>
			</div>
			<button
				type="button"
				onClick={close}
				className="rounded-md p-1 text-success-soft-foreground/70 hover:bg-success/10 hover:text-success-soft-foreground"
				aria-label="Dismiss"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
